import { useEffect, useState, useCallback, useRef } from 'react';
import type { Channel } from '@anycable/web';
import { getCable } from '../cable';

interface EventHandler {
  (data: any): void;
}

interface UseEventsSubscriptionOptions {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  eventHandlers?: Record<string, EventHandler>;
}

export function useCableSubscription(userId: string, options: UseEventsSubscriptionOptions = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionRef = useRef<Channel | null>(null);
  const eventHandlersRef = useRef<Record<string, EventHandler>>({});
  const pendingEventsRef = useRef<Record<string, any[]>>({});

  // Initialize event handlers from options
  useEffect(() => {
    if (options.eventHandlers) {
      eventHandlersRef.current = { ...options.eventHandlers };
    }
  }, []);

  useEffect(() => {
    const cable = getCable();
    if (!cable || !userId) return;

    const subscription = cable.subscribeTo('EventsChannel', { user_id: userId });
    subscriptionRef.current = subscription;

    subscription.on('connect', () => {
      setIsConnected(true);
      options.onConnect?.();
    });

    subscription.on('disconnect', () => {
      setIsConnected(false);
      options.onDisconnect?.();
    });

    subscription.on('message', (data: any) => {
      try {
        // Support both server formats: { message: "{...}" } and direct object { type, payload }
        const raw = typeof data?.message !== 'undefined' ? data.message : data;
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (parsed) {
          const inferredType =
            parsed.type ||
            parsed.event ||
            (parsed.thread_id && parsed.conversation_id ? 'real_time_message' : undefined);
          const eventType = inferredType;
          const payload = parsed.payload || parsed;

          const handler = eventHandlersRef.current[eventType];
          if (handler) {
            handler(payload);
          } else {
            // Buffer the event until a handler is registered
            pendingEventsRef.current[eventType] = pendingEventsRef.current[eventType] || [];
            pendingEventsRef.current[eventType].push(payload);
            const consol = console;
            consol.warn(`No handler registered for event type: ${eventType}`);
          }
        }
      } catch (error) {
        console.error('Error processing message:', error);
      }
    });

    return () => {
      subscription.disconnect();
      subscriptionRef.current = null;
    };
  }, [userId]);

  const addEventHandler = useCallback((eventType: string, handler: EventHandler) => {
    eventHandlersRef.current = {
      ...eventHandlersRef.current,
      [eventType]: handler,
    };
    // Flush any buffered events for this type
    const buffered = pendingEventsRef.current[eventType];
    if (buffered && buffered.length > 0) {
      buffered.forEach((payload) => {
        try {
          handler(payload);
        } catch (e) {
          console.error('Error handling buffered event', e);
        }
      });
      pendingEventsRef.current[eventType] = [];
    }
  }, []);

  const removeEventHandler = useCallback((eventType: string) => {
    const newHandlers = { ...eventHandlersRef.current };
    delete newHandlers[eventType];
    eventHandlersRef.current = newHandlers;
  }, []);

  return {
    isConnected,
    addEventHandler,
    removeEventHandler,
  };
}
